import React, { useState, useEffect } from 'react';
import { Plus, Search, Ticket, MessageSquare, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';
import { ApiService } from '../../../services/api';
import { Ticket as TicketType } from '../../../types/api';

const TicketManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchTickets();
  }, [page, pageSize]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getTickets({ page, pageSize });
      setTickets(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (data: { subject: string; description: string }) => {
    try {
      await ApiService.createTicket({
        subject: data.subject,
        description: data.description,
        priority: 'medium'
      });
      setShowCreateModal(false);
      fetchTickets();
    } catch (error) {
      console.error('Failed to create ticket:', error);
    }
  };

  const getStatusBadgeClass = (status: TicketType['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return '';
    }
  };

  const getStatusIcon = (status: TicketType['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <MessageSquare className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: TicketType['status']) => {
    switch (status) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'resolved':
        return '已解决';
      case 'closed':
        return '已关闭';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* 页面标题 */}
        <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-5">
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                  <Ticket className="w-7 h-7 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                  工单管理
                </h1>
                <p className="text-gray-600 mt-2 text-base leading-relaxed">管理客户服务请求和技术支持工单</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="group flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
              <span>新建工单</span>
            </button>
          </div>
        </div>

        {/* 搜索栏和统计 */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* 统计卡片 */}
          <div className="lg:col-span-1 grid grid-cols-2 lg:grid-cols-1 gap-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">待处理</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {tickets.filter(t => t.status === 'pending').length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">处理中</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {tickets.filter(t => t.status === 'processing').length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* 搜索栏 */}
          <div className="lg:col-span-3">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg p-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="搜索工单号、主题或用户名..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 工单列表 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
          <div className="p-6 border-b border-gray-200/50">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
              工单列表
            </h2>
          </div>

          {tickets.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Ticket className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">暂无工单</h3>
              <p className="text-gray-500 mb-6">点击"新建工单"按钮创建新的工单</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                <span>新建工单</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-hide">
              <table className="w-full table-fixed min-w-[600px] lg:min-w-[1200px]">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="w-[120px] lg:w-[140px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">工单号</th>
                    <th className="w-[100px] lg:w-[140px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">用户名</th>
                    <th className="w-[200px] lg:w-[300px] px-3 lg:px-6 py-4 text-left text-xs lg:text-sm font-semibold text-gray-900">主题</th>
                    <th className="w-[80px] lg:w-[120px] px-3 lg:px-6 py-4 text-center text-xs lg:text-sm font-semibold text-gray-900">状态</th>
                    <th className="w-[100px] lg:w-[140px] px-3 lg:px-6 py-4 text-center text-xs lg:text-sm font-semibold text-gray-900">处理人</th>
                    <th className="w-[120px] lg:w-[180px] px-3 lg:px-6 py-4 text-center text-xs lg:text-sm font-semibold text-gray-900">创建时间</th>
                    <th className="w-[160px] px-6 py-4 text-center text-sm font-semibold text-gray-900">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200/50">
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                      <td className="px-6 py-4">
                        <div className="font-mono text-sm font-semibold text-blue-600">#{ticket.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {ticket.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-gray-900 font-medium">{ticket.username}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 truncate">{ticket.subject}</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <div className={`p-1 rounded-lg ${getStatusBadgeClass(ticket.status).replace('text-', 'text-').replace('bg-', 'bg-')}`}>
                            {getStatusIcon(ticket.status)}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(ticket.status)}`}>
                            {getStatusText(ticket.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-gray-600 font-medium">{ticket.handler || '未分配'}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-gray-600 text-sm font-medium">{ticket.createdAt}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200" title="查看详情">
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 分页 */}
          {tickets.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200/50 bg-gray-50/50 px-6 py-4 rounded-b-2xl">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">每页行数：</span>
                <select 
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  显示 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} 共 {total} 条
                </span>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    首页
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    下一页
                  </button>
                  <button
                    onClick={() => setPage(Math.ceil(total / pageSize))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketManagement;
